import type { ZeovitRecommendationListItem } from '../services/zeovitRecommendationService';

function toTime(iso: string): number {
  const time = new Date(iso).getTime();
  return Number.isNaN(time) ? 0 : time;
}

/** Newest first — same order as the API (createdAt desc, then iteration desc). */
function sortNewestFirst(items: readonly ZeovitRecommendationListItem[]): ZeovitRecommendationListItem[] {
  return [...items].sort(
    (a, b) => toTime(b.createdAt) - toTime(a.createdAt) || b.iterationNumber - a.iterationNumber,
  );
}

/** One recommendation history (lineage): an iteration-1 root and its later iterations. */
export interface ZeovitLineage {
  readonly rootId: string;
  /** Newest first. */
  readonly items: ZeovitRecommendationListItem[];
}

/**
 * Groups the aquarium's recommendations into histories by following
 * parentRecommendationId up to the root ("Start new recommendation" begins a new
 * history; the old ones stay). Histories are ordered by their newest iteration, so
 * the first one is the current history.
 */
export function groupLineages(items: readonly ZeovitRecommendationListItem[]): ZeovitLineage[] {
  const byId = new Map(items.map((item) => [item.id, item] as const));
  const rootOf = (item: ZeovitRecommendationListItem): string => {
    let current = item;
    const seen = new Set<string>([current.id]);
    while (current.parentRecommendationId) {
      const parent = byId.get(current.parentRecommendationId);
      if (!parent || seen.has(parent.id)) break;
      seen.add(parent.id);
      current = parent;
    }
    return current.id;
  };
  const groups = new Map<string, ZeovitRecommendationListItem[]>();
  for (const item of sortNewestFirst(items)) {
    const root = rootOf(item);
    groups.set(root, [...(groups.get(root) ?? []), item]);
  }
  // Map insertion order follows the newest item of each group (items are sorted newest first).
  return [...groups.entries()].map(([rootId, groupItems]) => ({ rootId, items: groupItems }));
}

